import { FALLBACK_TZ } from '../../config/env';

const reportingYearFormatter = new Intl.DateTimeFormat('en', {
  timeZone: FALLBACK_TZ,
  year: 'numeric',
});

export const REPORTING_YEAR_BASELINE = 2024;
export const MANAGEMENT_REPORTING_YEAR_BASELINE = 2026;
export const REPORTING_YEAR_FORWARD_SPAN = 20;
export const HISTORICAL_VIEW_YEARS = [2025, 2024, 2023, 2022, 2021];

export const isHistoricalViewerYear = year => HISTORICAL_VIEW_YEARS.includes(Number(year));

export function reportingCalendarYear(now = new Date()) {
  const reportingYear = Number(reportingYearFormatter.format(now));
  return Number.isFinite(reportingYear) ? reportingYear : now.getUTCFullYear();
}

export function reportingYearWindow(now = new Date()) {
  const currentYear = reportingCalendarYear(now);
  const latestYear = currentYear + 1;
  if (latestYear < MANAGEMENT_REPORTING_YEAR_BASELINE) return [];
  const yearCount = latestYear - MANAGEMENT_REPORTING_YEAR_BASELINE + 1;
  return Array.from({ length: yearCount }, (_, index) => latestYear - index);
}

/**
 * Return selectable reporting years from the current Japan calendar year through
 * the configured future span, while retaining explicitly supplied legacy values.
 */
export function reportingYearOptions({
  includeYears = [], now = new Date(), yearsAhead = REPORTING_YEAR_FORWARD_SPAN,
} = {}) {
  const currentYear = reportingCalendarYear(now);
  const normalizedYearsAhead = Number.isInteger(yearsAhead) && yearsAhead >= 0
    ? yearsAhead : REPORTING_YEAR_FORWARD_SPAN;
  const forwardYears = Array.from(
    { length: normalizedYearsAhead + 1 },
    (_, index) => currentYear + index,
  );
  const legacyYears = (Array.isArray(includeYears) ? includeYears : [])
    .filter(value => value !== '' && value !== null && value !== undefined)
    .map(Number)
    .filter(year => Number.isInteger(year) && year > 0);

  return [...new Set([...legacyYears, ...forwardYears])]
    .sort((left, right) => left - right);
}

export function viewerReportingYearOptions(now = new Date()) {
  const currentYear = reportingCalendarYear(now);
  if (currentYear < REPORTING_YEAR_BASELINE) return [];
  return Array.from(
    { length: currentYear - 2021 + 1 },
    (_, index) => currentYear - index,
  );
}

export function managementReportingYearOptions({ now = new Date(), yearsAhead = 1 } = {}) {
  const currentYear = reportingCalendarYear(now);
  const normalizedYearsAhead = Number.isInteger(yearsAhead) && yearsAhead >= 0
    ? yearsAhead : 1;
  const firstYear = Math.min(MANAGEMENT_REPORTING_YEAR_BASELINE, currentYear);
  const lastYear = currentYear + normalizedYearsAhead;
  return Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => lastYear - index,
  );
}
